import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  aiApprovals,
  aiDecide,
  aiDecideBatch,
  aiPasskeyOptions,
  aiPause,
  aiResume,
  aiStatus,
  aiUndo,
  type AiStatus,
  type ApprovalsResponse,
  type PublicKeyRequestOptionsJson,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';
import { useQuery, type QueryState } from '../useQuery';
import { getPasskeyAssertion, passkeysSupported } from '../auth/webauthn';

/**
 * The AI controls' shared state (Console V4, Spec V4 section 16): one status and one
 * approval queue for the whole shell, so the top bar's chip, AI status, the paused bar,
 * the Atlas panel and Guardian's Approvals all read the same answer and a decision clears
 * it everywhere at once. Served by the /assistant/* routes (src/aithora/ai-controls-routes.ts);
 * the role and building come from the session on the server.
 */

/** How often the chip and the queue refresh while nothing is happening. */
export const AI_POLL_MS = 30_000;

/** Roles with the approval queue and Activity (the design's four). */
const QUEUE_ROLES = new Set(['SUPER_ADMIN', 'OPERATOR', 'ADMIN', 'CAREGIVER', 'STAFF']);
/** Roles with the AI chip (Sales, Partner, Family, Resident and Home never). */
const CHIP_ROLES = new Set(['SUPER_ADMIN', 'OPERATOR', 'DEVELOPER', 'ADMIN', 'CAREGIVER', 'STAFF']);

export interface AiControls {
  status: QueryState<AiStatus>;
  approvals: QueryState<ApprovalsResponse>;
  /** This role draws the chip (before the status has loaded too). */
  chipRole: boolean;
  queueRole: boolean;
  /** AI status (the chip's popover) open. */
  statusOpen: boolean;
  setStatusOpen: (open: boolean) => void;
  /** The Pause AI sheet open. */
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  /** Ask the shell to open the Atlas panel on its queue (from AI status). */
  openQueue: () => void;
  pause: (reason: string) => Promise<void>;
  resume: (stop: 'tenant' | 'building', organizationId?: string) => Promise<void>;
  decide: (id: string, decision: 'approve' | 'edit' | 'decline', extra?: { fields?: Record<string, string>; reason?: string; passkey?: boolean }) => Promise<void>;
  approveBatch: (ids: string[]) => Promise<void>;
  /** V5: stop an approved email inside its 60 seconds (nothing is sent). */
  undo: (id: string) => Promise<void>;
  /** V5: the request a text's tap link opened (/#approve=<id>): first and outlined, with a banner. */
  focusId: string | null;
  clearFocus: () => void;
  /** The last write's refusal, in words; null when the last write went through. */
  error: string | null;
}

const Ctx = createContext<AiControls | null>(null);

export function useAiControls(): AiControls | null {
  return useContext(Ctx);
}

/** Said on an owner-only control a non-owner sees (the server's own refusal, in words). */
export const OWNER_ONLY = 'Only the owner can do this.';

/**
 * Whether the signed-in person is the owner, from the server's owner-by-email check
 * (GET /ai/status viewer.owner). False only when the server said so; null while unknown,
 * so nothing is held back before the answer arrives (the server still checks).
 */
export function useIsOwner(): boolean | null {
  const ai = useContext(Ctx);
  const v = ai?.status.data?.viewer;
  return v ? !!v.owner : null;
}

const messageOf = (e: unknown, fallback: string) => (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string' ? (e as { message: string }).message : fallback);

export function AiControlsProvider({ children, onOpenQueue }: { children: ReactNode; onOpenQueue: () => void }) {
  const { client, session } = useSession();
  const { role } = useConsoleRole();
  const chipRole = !!role && CHIP_ROLES.has(role);
  const queueRole = !!role && QUEUE_ROLES.has(role);
  const status = useQuery<AiStatus>((signal) => aiStatus(client, { signal }), [client, session?.token, role], { pollMs: AI_POLL_MS, enabled: chipRole });
  const approvals = useQuery<ApprovalsResponse>((signal) => aiApprovals(client, { signal }), [client, session?.token, role], { pollMs: AI_POLL_MS, enabled: queueRole });
  const [statusOpen, setStatusOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The text's tap link (/#approve=<id>) opens the Atlas panel on its queue with that request
  // first. The server re-checks it is still waiting and this person may act on it.
  const [focusId, setFocusId] = useState<string | null>(null);
  useEffect(() => {
    if (!queueRole) return;
    const m = (window.location.hash || '').match(/approve=([\w-]+)/);
    if (!m) return;
    setFocusId(m[1]);
    onOpenQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueRole]);
  const clearFocus = useCallback(() => {
    setFocusId(null);
    if (window.history && window.history.replaceState) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  const refresh = useCallback(() => {
    status.reload();
    approvals.reload();
  }, [status, approvals]);

  const pause = useCallback(async (reason: string) => {
    setError(null);
    try {
      await aiPause(client, { body: reason.trim() ? { reason: reason.trim() } : {} });
    } catch (e) {
      setError(messageOf(e, 'The AI was not paused.'));
    }
    refresh();
  }, [client, refresh]);

  const resume = useCallback(async (stop: 'tenant' | 'building', organizationId?: string) => {
    setError(null);
    try {
      await aiResume(client, { body: { stop, ...(organizationId ? { organizationId } : {}) } });
    } catch (e) {
      setError(messageOf(e, 'The AI was not turned back on.'));
    }
    refresh();
  }, [client, refresh]);

  const decide = useCallback(async (id: string, decision: 'approve' | 'edit' | 'decline', extra: { fields?: Record<string, string>; reason?: string; passkey?: boolean } = {}) => {
    setError(null);
    try {
      let webauthnAssertion: unknown;
      if (extra.passkey) {
        if (!passkeysSupported()) throw new Error('This browser cannot use a passkey. Approve care actions from a device with Face ID, Windows Hello or a security key.');
        const o = await aiPasskeyOptions(client);
        webauthnAssertion = await getPasskeyAssertion(o.options as unknown as PublicKeyRequestOptionsJson);
      }
      await aiDecide(client, { params: { id }, body: { decision, ...(extra.fields ? { fields: extra.fields } : {}), ...(extra.reason ? { reason: extra.reason } : {}), ...(webauthnAssertion ? { webauthnAssertion } : {}) } });
    } catch (e) {
      setError(messageOf(e, 'That decision was not recorded.'));
    }
    refresh();
  }, [client, refresh]);

  const approveBatch = useCallback(async (ids: string[]) => {
    setError(null);
    try {
      await aiDecideBatch(client, { body: { ids } });
    } catch (e) {
      setError(messageOf(e, 'Those approvals were not recorded.'));
    }
    refresh();
  }, [client, refresh]);

  const undo = useCallback(async (id: string) => {
    setError(null);
    try {
      await aiUndo(client, { params: { id } });
    } catch (e) {
      setError(messageOf(e, 'Too late to undo: it has already gone.'));
    }
    refresh();
  }, [client, refresh]);

  const value = useMemo<AiControls>(() => ({
    status, approvals, chipRole, queueRole, statusOpen, setStatusOpen, sheetOpen, setSheetOpen,
    openQueue: () => { setStatusOpen(false); onOpenQueue(); },
    pause, resume, decide, approveBatch, undo, focusId, clearFocus, error,
  }), [status, approvals, chipRole, queueRole, statusOpen, sheetOpen, onOpenQueue, pause, resume, decide, approveBatch, undo, focusId, clearFocus, error]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** "1:04 PM" (the design's hm). */
export const hm = (iso: string | number) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Is anything paused that this viewer is told about? Drives the chip, the bar and the queue note. */
export function pausedFacts(s: AiStatus | null) {
  const tenant = s && s.tenant.paused ? s.tenant : null;
  const building = s?.building?.paused ? s.building : null;
  return {
    tenant,
    building,
    /** The chip reads AI paused: the Aithora stop, or a building role's own building. */
    chipPaused: !!tenant || (!!building && !!s?.viewer.building),
  };
}
